import { ChangeDetectionStrategy, Component, ElementRef, Host, OnInit, Renderer2 } from '@angular/core';
import { FormControl } from '@angular/forms';
import { format, parse } from 'date-fns';
import { GasDynamicBaseComponent } from '../../../core/gas-dynamic-base/gas-dynamic-base.component';
import { DateFormat } from '../../../utils/gas-date-directive/date-format.type';
import { DAYS, GasDatepicker, GasDatepickerTime, GasPickerDay, MONTH } from '../../gas-datepicker';
import { GasDatepickerInputHelper } from '../gas-datepicker-input-helper';
import {
  GasClickOutsideDirective,
} from '../../../utils/click-outside-directive/gas-click-outside.directive';

@Component({
  selector: 'gas-datapicker-calendar',
  templateUrl: 'gas-datapicker-calendar.component.html',
  styleUrls: ['gas-datapicker-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GasClickOutsideDirective,
  ],
})
export class GasDatapickerCalendarComponent extends GasDynamicBaseComponent<void> implements OnInit {

  /** Массив с днями для рендера в pickere */
  public daysInMonth: GasPickerDay[] = [];
  /** массив с днями недели */
  public weeks: string[] = DAYS;
  /** массив с месяцами */
  public month: string[] = MONTH;
  /** Объект хранящий актуальные значения даты */
  public dateSetting: GasDatepicker;
  /** Показывает календарь/список годов/список месяцев */
  public currentViewPicker: 'datepicker' | 'yearpicker' | 'monthpicker' = 'datepicker';
  /** флаг для отображения поля ввода времени */
  public timesPicker = false;
  public timeWithoutSeconds = false;
  public defaultTime: GasDatepickerTime;
  /** минимально доступный для выбора день */
  public minDate: Date;
  /** максимально доступный для выбора день */
  public maxDate: Date;
  public format: DateFormat = 'dd.MM.yyyy';
  public inputForm: FormControl;

  public set presetDate(presetDate: Date | string) {
    if (this.inputForm?.value && this.inputForm.valid) {
      this.selectedDate = parse(this.inputForm?.value, this.format, new Date());
      return;
    }

    if (presetDate) {
      this.selectedDate = typeof presetDate === 'string'
        ? parse(presetDate, this.format, new Date())
        : presetDate;
      return;
    }

    this.selectedDate = parse(format(new Date(), this.format), this.format, new Date());
  }

  /** Возвращает выбранную дату */
  public get presetDate(): Date | string {
    return this.selectedDate;
  }

  /** Выбранная дата */
  private selectedDate: Date;

  constructor(
    public el: ElementRef,
    @Host() public gasClickOutsideDirective: GasClickOutsideDirective,
    public renderer: Renderer2,
  ) {
    super(el, gasClickOutsideDirective, renderer);
  }

  public ngOnInit() {
    this.updateDate();
  }

  /** Возвращает текущий месяц */
  public get currentMonth(): number {
    return this.dateSetting.currentMonth;
  }

  /** Возвращает текущий год */
  public get currentYear(): string {
    return this.dateSetting.currentYear.toString();
  }

  /** обрабатываем изменение года */
  public onChangeYear(year: number): void {
    this.currentViewPicker = 'datepicker';
    this.dateSetting.currentYear = year;
    this.changeRenderPicker(this.presetDate as Date ?? undefined);
  }

  /** обрабатываем изменение месяца из отдельного блока */
  public changeCurrentMonth(month: number): void {
    this.currentViewPicker = 'datepicker';
    this.dateSetting.currentMonth = month;
    this.changeRenderPicker(this.presetDate as Date ?? undefined);
  }

  /**
   * Обновляет данные в календаре и объекте с актуальными значениями даты
   *
   * @param day - текущий выбранный день
   */
  public changeRenderPicker(day: Date = new Date()): void {
    this.dateSetting = GasDatepickerInputHelper.createCurrentDateObject(day, this.dateSetting.currentMonth, this.dateSetting.currentYear);
    this.daysInMonth = GasDatepickerInputHelper.setDaysInMonth(this.dateSetting, new Date(this.minDate), new Date(this.maxDate));
  }

  /** Выбираем предыдущий месяц */
  public previousMonth(index: number): void {
    this.changeMonth(index);
  }

  /** Выбираем следующий месяц */
  public nextMonth(index: number): void {
    this.changeMonth(index);
  }

  /**
   * Меняет текущий месяц
   *
   * @param index - индекс выбранного месяца
   */
  public changeMonth(index: number): void {
    this.dateSetting.currentMonth += index;
    this.checkAndSetMonth();
    this.changeRenderPicker(this.presetDate as Date ?? undefined);
  }


  /**
   * проверяет текущий месяц и меняет в нем значение если он выходит за корректный диапазон
   * в случае выхода за диапазон еще меняет год
   */
  public checkAndSetMonth(): void {
    if (this.dateSetting.currentMonth < 0) {
      this.dateSetting.currentMonth = 11;
      this.dateSetting.currentYear -= 1;
    } else if (this.dateSetting.currentMonth > 11) {
      this.dateSetting.currentMonth = 0;
      this.dateSetting.currentYear += 1;
    }
  }

  /** открывает блок с выбором года */
  public openYearPicker(): void {
    this.currentViewPicker = 'yearpicker';
  }

  /** открывает блок с выбором месяца */
  public openMonthPicker(): void {
    this.currentViewPicker = 'monthpicker';
  }


  /******************* Методы по установке даты ************************************/

  /**
   * Выбираем день в календаре
   * Вначале проверяем, относится ли выбранный день к текущему месяцу, если нет - то меняем месяц
   * Если день из текущего месяца то просто устанавливаем его как выбранный
   *
   * @param day - выбранный день из календаря
   */
  public selectDay(day: GasPickerDay): void {
    if (!day.isCurrent) {
      return;
    }

    if (day.otherMonth) {
      this.dateSetting.currentMonth += day.monthIndex;
      this.checkAndSetMonth();
    }

    if (this.timesPicker) {
      this.changeRenderPicker(day.value);
      return;
    }

    if (GasDatepickerInputHelper.checkToDay(day.value)) {
      this.setToDay();
      return;
    }

    this.updateInputValue(day.value);
    this.closePicker();
  }

  /** Устанавливает дату на текущий день */
  public setToDay(): void {
    this.updateInputValue(new Date());
    this.closePicker();
  }

  /** Обновляет дату в директиве, изменяет массив с датами и закрывает picker */
  public updateDate(): void {
    this.dateSetting = GasDatepickerInputHelper.createCurrentDateObject(this.presetDate as Date ?? undefined);
    this.daysInMonth = [];
    this.changeRenderPicker(this.presetDate as Date ?? undefined);
  }

  public setDateAndTime(times: string[]): void {
    this.updateInputValue(
      new Date(
        this.dateSetting.currentYear,
        this.dateSetting.currentMonth,
        this.dateSetting.currentDay.getDate(),
        times[0] ? +times[0] : this.defaultTime.hours,
        times[1] ? +times[1] : this.defaultTime.minutes,
        times[2] ? +times[2] : this.defaultTime.seconds,
      ),
    );

    this.closePicker();
  }

  public closePicker(): void {
    this.close(null);
  }

  private updateInputValue(date: Date | string): void {
    const value: Date | string =
      typeof date === 'string' || !Boolean(date)
        ? date
        : format(date, this.format);

    this.inputForm.setValue(value);
  }
}
